import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Siren, X } from "@phosphor-icons/react";
import { fetchEvacAlerts } from "../lib/api";

const LS_KEY = "okwatch:gonow:dismissed";

function getDismissed() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
}

// Full-width red banner shown app-wide while a Level 3 GO NOW evac is
// active. Dismiss is per-alert and re-triggers if the alert is updated.
export default function GoNowBanner() {
  const [alerts, setAlerts] = useState([]);
  const [, force] = useState(0);
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const load = () =>
      fetchEvacAlerts({})
        .then((d) => setAlerts((d || []).filter((a) => a.level === 3 && !a.lifted_at)))
        .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const dismissed = getDismissed();
  const visible = alerts.filter((a) => {
    const at = dismissed[a.id];
    if (!at) return true;
    const stamp = a.updated_at || a.created_at;
    return stamp && new Date(stamp).getTime() > at;
  });
  if (!visible.length) return null;

  const top = [...visible].sort(
    (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  )[0];
  const extra = visible.length - 1;

  const dismiss = () => {
    const d = getDismissed();
    const now = Date.now();
    visible.forEach((a) => { d[a.id] = now; });
    localStorage.setItem(LS_KEY, JSON.stringify(d));
    force((n) => n + 1);
  };

  return (
    <div
      data-testid="go-now-banner"
      className="w-full bg-[#E63946] text-white flex items-center gap-3 px-3 sm:px-4 py-2 gonow-flash"
    >
      <Siren size={18} weight="fill" className="shrink-0" />
      <button
        type="button"
        data-testid="go-now-banner-body"
        onClick={() => { if (loc.pathname !== "/") navigate("/"); }}
        className="flex-1 min-w-0 text-left"
      >
        <span className="text-[11px] sm:text-[12px] font-black uppercase tracking-[0.15em] mr-2">
          Level 3 — GO NOW
        </span>
        <span className="text-[12px] sm:text-[13px] font-semibold">
          {top.title}
          {top.county && !top.title.toLowerCase().includes(top.county.replace("_", " ").toLowerCase())
            ? ` · ${top.county.replace("_", " ")} Co.`
            : ""}
          {extra > 0 ? ` · +${extra} more Level 3` : ""}
        </span>
      </button>
      {loc.pathname !== "/" && (
        <button
          type="button"
          onClick={() => navigate("/")}
          className="hidden sm:inline text-[11px] font-bold uppercase tracking-wider underline underline-offset-2 shrink-0"
        >
          View map
        </button>
      )}
      <button
        type="button"
        data-testid="go-now-banner-dismiss"
        onClick={dismiss}
        title="Dismiss (returns if the alert is updated)"
        className="shrink-0 opacity-80 hover:opacity-100"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
